<template>
    <main class="layout__main--narrow layout__main--no-top-padding">
        <BaseHeader
            v-if="pending || headerImage"
            :placeholder="pending"
            :image="headerImage"
        />
        <article v-if="event">
            <BaseSection>
                <h1>{{ event.title }}</h1>

                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-if="event.intro"
                    class="lead"
                    data-allow-anchor
                    v-html="event.intro"
                />
                <!-- eslint-enable vue/no-v-html -->

                <BaseEventMeta :event />

                <!-- todo press releases ? -->
                <!-- <press-releases-meta :press-releases="page" /> -->
                <BasePillsList :pills="pills(event, event)" />
            </BaseSection>

            <BaseEventDetails :event />

            <BaseSection v-if="image">
                <BaseFigure
                    :src="image.intermediateUrl"
                    :srcset="getSrcSet(image, 'responsive')"
                    width="844"
                    sizes="100vw"
                    :alt="image.alt || ''"
                >
                    <template
                        v-if="event.caption"
                        #caption
                    >
                        {{ event.caption }}
                    </template>
                </BaseFigure>
            </BaseSection>

            <BaseSection v-if="event.description">
                <template #default>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        data-allow-anchor
                        v-html="sanitizedEventDescription"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </template>
            </BaseSection>

            <BaseSection v-if="event.website && event.typeHandle === 'live'">
                <template v-if="isRegistrationOpen">
                    <client-only>
                        <BaseButton
                            element="a"
                            :href="event.website"
                            target="_blank"
                            rel="noopener"
                        >
                            <template v-if="eventType && eventType !== 'Other'">
                                Join event <BaseIcon icon="arrow-up-right" />
                            </template>

                            <template v-else>
                                Register here <BaseIcon icon="arrow-up-right" />
                            </template>
                        </BaseButton>
                    </client-only>
                </template>

                <template v-if="!isRegistrationOpen && eventType && eventType === 'Masterclass'">
                    <p> {{ closedRegistrationMessage }} </p>
                </template>
            </BaseSection>

            <BaseSection v-if="event.eCourseId">
                <DoceboButton :id="event.eCourseId" />
            </BaseSection>

            <BaseSection v-if="event?.webinarUrl">
                <BaseEmbed :url="event.webinarUrl" />
            </BaseSection>

            <BaseSection v-if="event.faculty && event.faculty.length">
                <template #title>
                    <h2>Faculty</h2>
                </template>
                <BaseMemberList
                    :members="event.faculty"
                    :is-faculty="true"
                />
            </BaseSection>

            <BaseSection v-if="event.sponsorInformation">
                <template #title>
                    <h2>Acknowledgement</h2>
                </template>
                <!-- eslint-disable vue/no-v-html -->
                <div
                    data-allow-anchor
                    v-html="event.sponsorInformation"
                />
                <!-- eslint-enable vue/no-v-html -->
            </BaseSection>

            <BaseEventContact
                :event="event"
            />

            <BaseSection>
                <LazyBaseSocialLinks
                    type="event"
                    :hydrate-on-visible
                />
            </BaseSection>
        </article>

        <article v-else-if="pending">
            <BaseSection>
                <ContentPlaceholdersHeading />
                <ContentPlaceholdersText :lines="5" />
            </BaseSection>

            <BaseSection>
                <ContentPlaceholdersImg />
            </BaseSection>

            <BaseSection>
                <ContentPlaceholdersImg />
            </BaseSection>

            <BaseSection>
                <ContentPlaceholdersText :lines="10" />
            </BaseSection>
        </article>

        <BaseError
            v-else-if="error || notFound"
            :error="error || { statusCode: 404 }"
        />
    </main>
</template>

<script setup>
import { isAfter, isBefore, parseISO } from 'date-fns';
import EventDetail from '~/graphql/queries/events/Detail.graphql';
import useSrcSets from '~/utils/srcset';
import DoceboButton from '~/components/DoceboButton.vue';

const { activeSite } = useMultisite();
const { getSrcSet } = useSrcSets();

const { formatSingleDate, formats } = useDate();
const { pills } = usePillsPage();

const { variables } = useCraftGraphqlSlug();

const response = await useLazyAsyncQuery({
    query: EventDetail,
    variables,
});

const {
    craftEntry: event,
    headerImage,
    pending,
} = usePageQuery(
    response,
    { entryKey: 'event', activeSite }
);

const image = computed(() => {
    return event.value?.image?.[0];
});

const isRegistrationOpen = computed(() => {
    if (event.value?.registrationDateRange?.start && event.value?.registrationDateRange?.end) {
        const startDate = parseISO(event.value?.registrationDateRange?.start);
        const endDate = parseISO(event.value?.registrationDateRange?.end);

        return isAfter(new Date(), startDate) && isBefore(new Date(), endDate);
    }
    return true;
});

const eventType = computed(() => {
    return event.value?.type?.[0]?.title;
});

const closedRegistrationMessage = computed(() => {
    if (event.value?.registrationDateRange?.start && event.value?.registrationDateRange?.end) {
        const startDate = parseISO(event.value?.registrationDateRange?.start);
        const endDate = parseISO(event.value?.registrationDateRange?.end);
        let message = `Application for this masterclass is available from ${formatSingleDate(startDate, formats.dayMonthYear)}.`;

        if (isBefore(endDate, new Date())) {
            message = `Application for this masterclass is closed since ${formatSingleDate(endDate, formats.dayMonthYear)}.`;
        }

        return message;
    }
    return null;
});

const sanitizedEventDescription = computed(() => {
    if (!event.value?.description) {
        return;
    }

    // Hacky:
    // Remove all instances of <p> tags with only spans with whitespace
    return event.value.description.replace(/<p><span>\s*<\/span><\/p>/g, '');

});
</script>
